import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PasteListFlowHeader from "../components/PasteListFlowHeader";
import { extractTextFromFile } from "../utils/pasteListFileParser";
import { expandPasteLines, loadPasteListFlow, savePasteListFlow } from "../utils/pasteListFlow";

const SAMPLE_LIST = `Milk 2L
Bread
Coca Cola Zero 8pack
Weetabix 24 pack
Bananas
Eggs x 10
Pasta 500g
Butter 250g`;

function PasteListStep({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [pasteText, setPasteText] = useState("");
  const [selectedFileName, setSelectedFileName] = useState("");
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [fileError, setFileError] = useState("");

  useEffect(() => {
    if (location.state?.fromEditList) {
      const saved = loadPasteListFlow();
      const text = String(saved?.rawText || saved?.lines?.join("\n") || "").trim();
      setPasteText(text);
      setSelectedFileName(saved?.fileName || "");
      return;
    }
    setPasteText("");
    setSelectedFileName("");
  }, [location.key, location.state?.fromEditList]);

  const handleContinue = () => {
    const lines = expandPasteLines(pasteText);
    if (!lines.length) return;
    const existing = loadPasteListFlow();
    savePasteListFlow({
      ...existing,
      lines,
      rawText: pasteText,
      fileName: selectedFileName || undefined,
      matches: [],
      products: [],
      totalCount: 0,
      queryList: "",
    });
    setPasteText("");
    setSelectedFileName("");
    navigate("/paste-list/review");
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileError("");
    setSelectedFileName(file.name);
    setIsParsingFile(true);
    try {
      const extracted = await extractTextFromFile(file);
      setPasteText(extracted);
    } catch (err) {
      setSelectedFileName("");
      setFileError(
        err?.message || "Unable to read this file. Please upload a text file (.txt, .pdf, .doc, or .docx)."
      );
    } finally {
      setIsParsingFile(false);
      if (e.target) e.target.value = "";
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#F5F7FB] text-[#24303A]">
      <Header onOpenAuth={onOpenAuth} onOpenMenu={onOpenMenu} />
      <PasteListFlowHeader currentStep={1} />

      <main className="mx-auto w-full max-w-6xl flex-1 px-3 py-5 sm:px-4 sm:py-6 md:px-6">
        <div className="rounded-2xl border border-[#E6EAF0] bg-white p-4 shadow-sm sm:p-6">
          <h2 className="text-[20px] font-semibold text-[#24303A] sm:text-[22px]">
            Paste your grocery list
          </h2>
          <p className="mt-1 text-[14px] text-[#6E7B86] sm:text-[16px]">
            Add items line by line. One item per line.
          </p>

          <textarea
            value={pasteText}
            onChange={(e) => setPasteText(e.target.value)}
            placeholder={SAMPLE_LIST}
            className="mt-4 h-56 w-full resize-none rounded-2xl border-2 border-[#E6EAF0] bg-white px-4 py-3 text-base text-[#24303A] outline-none focus:border-[#0d9499] sm:h-72"
          />

          <p className="mt-4 text-center text-[14px] font-semibold text-[#6E7B86] sm:text-[16px]">
            or import a file
          </p>

          <label className="mt-3 flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-[#D7DEE8] bg-[#FAFBFD] px-4 py-8 text-center transition hover:border-[#0d9499] hover:bg-[#F0FAFA]">
            <svg viewBox="0 0 24 24" className="mb-2 h-8 w-8 text-[#0d9499]" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9z" strokeLinecap="round" />
              <path d="M14 3v6h6M12 18v-6M9 15l3-3 3 3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-[14px] font-semibold text-[#24303A] sm:text-[20px]">
              {isParsingFile ? "Reading file..." : "Drop a file here or click to browse"}
            </span>
            <span className="mt-1 text-[14px] text-[#6E7B86] sm:text-[16px]">
              Supports .txt, .pdf, .doc, .docx
            </span>
            <input
              type="file"
              accept=".txt,.pdf,.doc,.docx,text/plain,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              disabled={isParsingFile}
              onChange={handleFileChange}
            />
          </label>

          {selectedFileName && !fileError && (
            <p className="mt-2 text-[13px] font-medium text-[#6E7B86]">{selectedFileName}</p>
          )}
          {fileError && (
            <p className="mt-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[13px] text-red-700 sm:text-[14px]">
              {fileError}
            </p>
          )}

          <div className="mt-6 flex justify-center">
            <button
              type="button"
              disabled={!String(pasteText || "").trim() || isParsingFile}
              onClick={handleContinue}
              className="rounded-full bg-[#7352C7] px-8 py-2 text-[16px] font-semibold text-white shadow-sm disabled:opacity-50 sm:text-[20px]"
            >
              Search
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default PasteListStep;
